/**
 * Analytics Dashboard
 * Real enrichment cost analytics and scheduling recommendations
 * Reads from provider_import_jobs to show actual spend and coverage trends
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Lightbulb,
  CheckCircle,
  Activity,
  Globe,
  Phone,
  Mail,
  MapPin,
  FileText,
  Zap,
  Clock
} from 'lucide-react';
import { config } from '@/src/config';

const getApiBaseUrl = () => {
  // Desktop (Tauri) has no Vercel proxy; use the absolute service URL there.
  if (config.isProduction && !config.isDesktop) return '/provider-api';
  return config.providerApi.baseUrl;
};

const API_BASE = getApiBaseUrl();

interface JobSummary {
  id: number;
  source_name: string;
  source_type: string;
  status: string;
  total_records: number;
  imported_records: number;
  progress_detail: any;
  created_at: string;
  completed_at: string | null;
}

const AnalyticsDashboard: React.FC = () => {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/import/cbo/import/jobs?limit=100`),
        fetch(`${API_BASE}/import/cbo/stats`),
      ]);
      if (jobsRes.ok) {
        const d = await jobsRes.json();
        setJobs(d.jobs || []);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (e) {
      console.error('Analytics load error:', e);
    } finally {
      setLoading(false);
    }
  };

  // Provider import / enrichment jobs (drives the activity feed + recommendations).
  // Cost/usage tracking lives in the External Services Catalog (real per-service
  // metering), so this page no longer fabricates spend from job progress_detail.
  const enrichmentJobs = jobs.filter(j =>
    ['contact_waterfall', 'form990_enrichment', 'geocoding', 'ntee_enrichment', 'enrichment', 'CMS_NPPES', 'irs_bulk'].includes(j.source_type || j.source_name)
  );

  // Coverage from stats
  const total = stats?.total_providers || 0;
  const withPhone = stats?.with_phone || 0;
  const withEmail = stats?.with_email || 0;
  const withWebsite = stats?.with_website || 0;
  const geocoded = stats?.geocoded || 0;
  const withServices = stats?.with_services || 0;

  // Active jobs by type
  const activeByType: Record<string, { running: number; completed: number; totalRecords: number }> = {};
  for (const job of enrichmentJobs) {
    const type = job.source_type || job.source_name || 'unknown';
    if (!activeByType[type]) activeByType[type] = { running: 0, completed: 0, totalRecords: 0 };
    if (job.status === 'RUNNING' || job.status === 'QUEUED') activeByType[type].running += 1;
    if (job.status === 'COMPLETED') activeByType[type].completed += 1;
    activeByType[type].totalRecords += job.total_records || 0;
  }

  // Compute recommendations — dynamic based on coverage AND active jobs
  const recommendations: Array<{ title: string; description: string; impact: string; action: string; priority: 'high' | 'medium' | 'low'; status?: string }> = [];

  const missingWebsite = total - withWebsite;
  const missingEmail = total - withEmail;
  const missingPhone = total - withPhone;
  const missingGeo = total - geocoded;

  if (missingPhone > 500000) {
    const a = activeByType['form990_enrichment'];
    const status = a?.running ? `In progress (${a.running} job${a.running > 1 ? 's' : ''} running)` :
      a?.completed ? `${a.completed} runs completed — try earlier years (2023, 2022, 2021)` : undefined;
    recommendations.push({
      title: 'Run Form 990 enrichment for more years',
      description: `${missingPhone.toLocaleString()} providers still missing phone. Each year covers different filers.`,
      impact: 'Free — IRS bulk XML data',
      action: 'form990',
      priority: 'high',
      status,
    });
  }

  if (missingWebsite > 100000) {
    const a = activeByType['contact_waterfall'];
    const estCost = missingWebsite * 0.001;
    const status = a?.running ? `In progress (${a.running} job${a.running > 1 ? 's' : ''} running)` :
      a?.completed ? `${a.completed} runs completed — ${a.totalRecords.toLocaleString()} processed so far` : undefined;
    recommendations.push({
      title: 'Run Brave Search for website discovery',
      description: `${missingWebsite.toLocaleString()} providers missing websites. At 59% discovery rate, expect ~${Math.round(missingWebsite * 0.59).toLocaleString()} websites.`,
      impact: `~$${estCost.toFixed(0)} for ${Math.ceil(missingWebsite / 10000)} batches of 10,000`,
      action: 'contact-waterfall',
      priority: 'high',
      status,
    });
  }

  if (missingEmail > 100000 && withWebsite > 50000) {
    recommendations.push({
      title: 'Run Hunter.io for email discovery',
      description: `${withWebsite.toLocaleString()} providers have websites but ${missingEmail.toLocaleString()} are missing email.`,
      impact: `~$${(Math.min(missingEmail, withWebsite) * 0.01).toFixed(0)} at $0.01/query`,
      action: 'contact-waterfall',
      priority: 'medium',
    });
  }

  if (missingGeo > 100000) {
    const a = activeByType['geocoding'];
    const status = a?.running ? `In progress (${a.running} running)` :
      a?.completed ? `${a.completed} runs completed` : undefined;
    recommendations.push({
      title: 'Run geocoding for map coverage',
      description: `${missingGeo.toLocaleString()} providers not geocoded. Census Bureau API is free.`,
      impact: 'Free — batch 50,000-100,000 at a time',
      action: 'geocoding',
      priority: 'medium',
      status,
    });
  }

  const fmt = (n: number) => n.toLocaleString();
  const fmtPct = (n: number, d: number) => d > 0 ? `${Math.round(n / d * 100)}%` : '0%';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <BarChart3 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Import & Enrichment Analytics</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Coverage gaps and scheduling recommendations · cost &amp; usage in the External Services Catalog</p>
          </div>
        </div>
        <button onClick={loadData} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {/* Coverage Overview */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { label: 'Total Providers', value: fmt(total), icon: <Activity className="h-4 w-4" />, color: 'blue' },
              { label: 'With Phone', value: `${fmt(withPhone)} (${fmtPct(withPhone, total)})`, icon: <Phone className="h-4 w-4" />, color: 'green' },
              { label: 'With Website', value: `${fmt(withWebsite)} (${fmtPct(withWebsite, total)})`, icon: <Globe className="h-4 w-4" />, color: 'teal' },
              { label: 'With Email', value: `${fmt(withEmail)} (${fmtPct(withEmail, total)})`, icon: <Mail className="h-4 w-4" />, color: 'purple' },
              { label: 'Geocoded', value: `${fmt(geocoded)} (${fmtPct(geocoded, total)})`, icon: <MapPin className="h-4 w-4" />, color: 'orange' },
              { label: 'With Services', value: `${fmt(withServices)} (${fmtPct(withServices, total)})`, icon: <FileText className="h-4 w-4" />, color: 'indigo' },
            ].map(item => (
              <div key={item.label} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-${item.color}-600`}>{item.icon}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{item.label}</span>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{item.value}</p>
              </div>
            ))}
          </div>

          {/* CBO / provider import quality metrics (moved here from the import pages) */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { label: 'IRS Verified', value: fmt(stats?.irs_verified || 0) },
              { label: 'Manually Verified', value: fmt(stats?.manually_verified || 0) },
              { label: 'With EIN', value: `${fmt(stats?.with_ein || 0)} (${fmtPct(stats?.with_ein || 0, total)})` },
              { label: 'With NTEE', value: `${fmt(stats?.with_ntee_code || 0)} (${fmtPct(stats?.with_ntee_code || 0, total)})` },
              { label: 'Data Sources', value: fmt(stats?.unique_data_sources || 0) },
              { label: 'States Covered', value: fmt(stats?.states_covered || 0) },
              { label: 'Enriched', value: fmt(stats?.enriched || 0) },
              { label: 'Avg Confidence', value: stats?.avg_confidence_score != null ? Number(stats.avg_confidence_score).toFixed(2) : '—' },
            ].map(item => (
              <div key={item.label} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">{item.label}</span>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Recent Job Activity */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Recent Job Activity</h3>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {enrichmentJobs.slice(0, 15).map(job => {
                const type = (job.source_type || job.source_name || '').replace(/_/g, ' ');
                const statusColor = job.status === 'COMPLETED' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  job.status === 'RUNNING' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                  job.status === 'FAILED' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  'bg-gray-100 text-gray-700';

                return (
                  <div key={job.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-gray-500">#{job.id}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{type}</p>
                        <p className="text-xs text-gray-500">{fmt(job.total_records || 0)} records</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>{job.status}</span>
                  </div>
                );
              })}
              {enrichmentJobs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No import or enrichment jobs yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Smart Recommendations */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Scheduling & Coverage Recommendations</h3>
            </div>

            {recommendations.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-400">All coverage targets met. No recommendations at this time.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommendations.map((rec, idx) => (
                  <div key={idx} className={`border rounded-lg p-4 ${
                    rec.priority === 'high' ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10' :
                    rec.priority === 'medium' ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10' :
                    'border-gray-200 dark:border-gray-700'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">{rec.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        rec.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                        rec.priority === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                        'bg-gray-100 text-gray-700'
                      }`}>{rec.priority}</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{rec.description}</p>
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <DollarSign className="h-3 w-3 text-gray-400" />
                      <span className="text-gray-500 dark:text-gray-400">{rec.impact}</span>
                    </div>
                    {rec.status && (
                      <div className="flex items-center gap-1.5 text-xs mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        {rec.status.includes('progress') ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 flex-shrink-0"></div>
                        ) : (
                          <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        )}
                        <span className="text-gray-600 dark:text-gray-300">{rec.status}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Scheduling Tips */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-1">Scheduling Tips</p>
                  <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <li>&#8226; Run Form 990 enrichment first (free) — it provides phone/website for ~12% of CBOs</li>
                    <li>&#8226; Then run Brave Search waterfall to discover websites for remaining providers</li>
                    <li>&#8226; Run geocoding last — it only needs address data, not contact info</li>
                    <li>&#8226; Schedule waterfall jobs during off-peak hours (2-6 AM) for best API response times</li>
                    <li>&#8226; Budget $50-100 per 10,000-provider waterfall batch ($0.005-0.01 per provider)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
